import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { ArrowLeft } from "lucide-react";

const COLUMNS = [
  { key: "tarih", label: "Tarih", type: "date" },
  { key: "vade_tarihi", label: "Vade", type: "date" },
  { key: "aciklama", label: "Açıklama", type: "text" },
  { key: "icra_tutari", label: "İcra Tutarı", type: "money" },
  { key: "tahsilat", label: "Tahsilat", type: "money", color: "success" },
  { key: "kalan", label: "Kalan", type: "money" },
  { key: "brut_risk", label: "Brüt Risk", type: "money", color: "danger" },
  { key: "net_risk", label: "Net Risk", type: "money", color: "danger" },
];

export default function CustomerDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/customers/${encodeURIComponent(name)}`).then((res) => setData(res.data));
  }, [name]);

  const s = data?.summary || {};
  const records = data?.records || [];

  const cellColor = (col, val) => {
    if (col.color === "danger" && val > 0) return "text-[#A63A3A]";
    if (col.color === "success" && val > 0) return "text-[#2F6E4E]";
    if (col.color === "interest" && val > 0) return "text-[#7C5E9B]";
    return "text-[#26231E]";
  };

  return (
    <Layout>
      <button
        onClick={() => navigate(-1)}
        data-testid="customer-back-button"
        className="flex items-center gap-1.5 text-sm text-[#8C8377] hover:text-[#26231E] mb-4 transition-colors duration-150"
      >
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>

      <div className="kicker mb-1">CARİ DETAY</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-6" data-testid="customer-name-heading">
        {decodeURIComponent(name)}
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Stat label="Kayıt Sayısı" value={s.kayit_sayisi ?? 0} raw />
        <Stat label="Toplam İcra" value={fmt(s.toplam_icra)} />
        <Stat label="Toplam Tahsilat" value={fmt(s.toplam_tahsilat)} color="#2F6E4E" />
        <Stat label="Toplam Kalan" value={fmt(s.toplam_kalan)} />
        <Stat label="Brüt Risk" value={fmt(s.toplam_brut_risk)} color="#A63A3A" />
        <Stat label="Net Risk" value={fmt(s.toplam_net_risk)} color="#A63A3A" />
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-20rem)]" data-testid="customer-records-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2.5 kicker whitespace-nowrap ${c.type === "money" ? "text-right" : "text-left"}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-[#8C8377]">
                  Kayıt yok.
                </td>
              </tr>
            ) : (
              records.map((r, i) => (
                <tr key={r.id} className={`border-b border-[#E7DECC] ${i % 2 === 1 ? "bg-[#F3ECDD]/60" : ""}`}>
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 whitespace-nowrap ${
                        c.type === "money" ? `text-right tabular-nums ${cellColor(c, r[c.key])}` : ""
                      }`}
                    >
                      {c.type === "date" ? fmtDate(r[c.key]) : c.type === "money" ? fmt(r[c.key]) : r[c.key] || "-"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

function Stat({ label, value, raw, color }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4">
      <div className="kicker mb-1.5">{label}</div>
      <div className="font-heading font-bold text-lg tabular-nums" style={color ? { color } : undefined}>
        {value}
      </div>
    </div>
  );
}
